/**
 * TextureManager - loading and caching of textures.
 */

import * as THREE from 'three';
import { TextureLoader } from 'three';
import { DDSLoader } from 'three/addons/loaders/DDSLoader.js';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { TGALoader } from 'three/addons/loaders/TGALoader.js';
import { TIFFLoader } from 'three/addons/loaders/TIFFLoader.js';

import { NetRuntime } from '../multiplayer/NetRuntime.js';
import { UrlTexture } from '../render/Texture.js';
import { projectInfo, resolvePath } from '../storage/projectContext.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import { WorldSerializer } from '../systems/WorldSerialization.js';
import { isNode } from '../utils/Utils.js';

import type { IGenesysRenderer } from '../render/IGenesysRenderer.js';
import type { AssetPath } from '../storage/storageProvider.js';

/**
 * Supported texture file formats.
 */
const enum TextureFormat {
  // Standard formats
  PNG = '.png',
  JPG = '.jpg',
  JPEG = '.jpeg',
  WEBP = '.webp',
  GIF = '.gif',
  BMP = '.bmp',
  TIFF = '.tiff',
  // HDR formats
  HDR = '.hdr',
  EXR = '.exr',
  // Compressed/complex formats
  KTX2 = '.ktx2',
  DDS = '.dds',
  TGA = '.tga',
  // Genesys serialized format
  TEXTURE_JSON = '.texture.json',
}

/**
 * Manages the loading and caching of textures.
 */
export class TextureManager {
  protected cachedTextures: Record<string, Promise<THREE.Texture | null>> = {};
  protected textureLoader: TextureLoader = new TextureLoader();
  protected hdrLoader: HDRLoader = new HDRLoader();
  protected exrLoader: EXRLoader = new EXRLoader();
  protected _ktx2Loader: KTX2Loader | null = null;
  protected ddsLoader: DDSLoader = new DDSLoader();
  protected tgaLoader: TGALoader = new TGALoader();
  protected tiffLoader: TIFFLoader = new TIFFLoader();

  constructor() {
    ObjectTracker.track(this);
    if (!isNode()) {
      const crossOrigin = 'anonymous';
      this.textureLoader.setCrossOrigin(crossOrigin);
      this.hdrLoader.setCrossOrigin(crossOrigin);
      this.exrLoader.setCrossOrigin(crossOrigin);
      this.ddsLoader.setCrossOrigin(crossOrigin);
      this.tgaLoader.setCrossOrigin(crossOrigin);
    }
  }

  /**
   * The KTX2 loader for loading KTX2 compressed textures.
   * Returns null if not initialized.
   */
  public get ktx2Loader(): KTX2Loader | null {
    return this._ktx2Loader;
  }

  /**
   * Initializes the KTX2 loader with the renderer.
   * Must be called before loading KTX2 textures. Uses the Three.js CDN
   * for the basis transcoder files by default.
   *
   * @param renderer - The native renderer to use for detecting KTX2 support
   */
  public initWithNativeRenderer(renderer: IGenesysRenderer): void {
    const native = renderer.asWebGL() ?? renderer.asWebGPU();
    if (native) {
      this._ktx2Loader = new KTX2Loader();
      this._ktx2Loader.setCrossOrigin('anonymous');
      // Construct full semver version from THREE.REVISION (e.g., "184" -> "0.184.0")
      const threeVersion = `0.${THREE.REVISION}.0`;
      this._ktx2Loader.setTranscoderPath(`https://unpkg.com/three@${threeVersion}/examples/jsm/libs/basis/`);
      this._ktx2Loader.detectSupport(native);
    }
  }

  /**
   * Detects the texture format from a file URL.
   *
   * @param url - The URL to check
   * @returns The detected texture format or null if unknown
   */
  protected detectFormat(url: string): TextureFormat | null {
    const lowerUrl = url.toLowerCase();

    // Check for double extensions first (e.g., .texture.json)
    if (lowerUrl.endsWith(TextureFormat.TEXTURE_JSON)) {
      return TextureFormat.TEXTURE_JSON;
    }

    // Single extension checks
    if (lowerUrl.endsWith(TextureFormat.KTX2)) return TextureFormat.KTX2;
    if (lowerUrl.endsWith(TextureFormat.DDS)) return TextureFormat.DDS;
    if (lowerUrl.endsWith(TextureFormat.TGA)) return TextureFormat.TGA;
    if (lowerUrl.endsWith(TextureFormat.HDR)) return TextureFormat.HDR;
    if (lowerUrl.endsWith(TextureFormat.EXR)) return TextureFormat.EXR;
    if (lowerUrl.endsWith(TextureFormat.PNG)) return TextureFormat.PNG;
    if (lowerUrl.endsWith(TextureFormat.JPG)) return TextureFormat.JPG;
    if (lowerUrl.endsWith(TextureFormat.JPEG)) return TextureFormat.JPEG;
    if (lowerUrl.endsWith(TextureFormat.WEBP)) return TextureFormat.WEBP;
    if (lowerUrl.endsWith(TextureFormat.GIF)) return TextureFormat.GIF;
    if (lowerUrl.endsWith(TextureFormat.BMP)) return TextureFormat.BMP;
    if (lowerUrl.endsWith(TextureFormat.TIFF)) return TextureFormat.TIFF;

    return null;
  }

  /**
   * Loads a texture using the appropriate loader for the file format.
   *
   * @param url - The URL of the texture to load
   * @param format - The texture format
   * @returns The loaded texture, or null if loading failed
   */
  protected async loadWithAppropriateLoader(
    url: string,
    format: TextureFormat
  ): Promise<THREE.Texture | null> {
    switch (format) {
      case TextureFormat.HDR:
        return await this.hdrLoader.loadAsync(url);

      case TextureFormat.EXR:
        return await this.exrLoader.loadAsync(url);

      case TextureFormat.KTX2:
        if (!this._ktx2Loader) {
          console.error(
            `[TextureManager] KTX2Loader not initialized, skipping KTX2 texture: ${url}`
          );
          return null;
        }
        return await this._ktx2Loader.loadAsync(url);

      case TextureFormat.DDS:
        return await this.ddsLoader.loadAsync(url);

      case TextureFormat.TGA:
        return await this.tgaLoader.loadAsync(url);

      case TextureFormat.TIFF:
        return await this.tiffLoader.loadAsync(url);

      case TextureFormat.PNG:
      case TextureFormat.JPG:
      case TextureFormat.JPEG:
      case TextureFormat.WEBP:
      case TextureFormat.GIF:
      case TextureFormat.BMP:
      default:
        return await this.textureLoader.loadAsync(url);
    }
  }

  /**
   * Gets the cached textures.
   *
   * @returns The cached textures
   */
  public getCachedTextures(): Record<string, Promise<THREE.Texture | null>> {
    return this.cachedTextures;
  }

  /**
   * Clears the cached textures.
   */
  public clearCache(): void {
    const oldCachedTextures = this.cachedTextures;
    this.cachedTextures = {};
    this.disposeTextures(oldCachedTextures);
  }

  private async disposeTextures(cachedTextures: Record<string, Promise<THREE.Texture | null>>) : Promise<void> {
    const settle = <T>(p: Promise<T | null>): Promise<T | null> => p.catch(() => null);
    const textures = await Promise.all(Object.values(cachedTextures).map(settle));
    for (const tex of textures) {
      tex?.dispose();
    }
  }

  /**
   * Invalidates cached textures.
   * Call this when textures have been modified and need to be reloaded.
   *
   * @param texturePath - Optional specific texture path to invalidate. If not provided, clears all texture cache.
   */
  public invalidateCache(texturePath?: AssetPath | string): void {
    if (texturePath) {
      const pathStr = typeof texturePath === 'string' ? texturePath : texturePath.initialPath;
      delete this.cachedTextures[pathStr];
    } else {
      this.cachedTextures = {};
    }
  }

  /**
   * Returns all pending texture loading promises.
   *
   * @returns Array of pending texture promises
   */
  public getPendingPromises(): Promise<THREE.Texture | null>[] {
    return Object.values(this.cachedTextures);
  }

  /**
   * Logs the cached textures.
   */
  public logCachedTextures(): void {
    console.log('[TextureManager] cached textures:', Object.keys(this.cachedTextures));
  }

  /**
   * Loads a texture from a URL.
   *
   * @param path - The path of the texture to load
   * @returns The loaded texture, or null if the texture could not be loaded
   */
  public async loadTexture(path: AssetPath): Promise<THREE.Texture | null> {
    // Skip texture loading on server
    if (NetRuntime.isDedicatedServer()) {
      // console.debug(`[TextureManager] Skipping texture load on server: ${path}`);
      return null;
    }

    if (isNode()) {
      return new THREE.Texture();
    }

    const rawUrl = path.initialPath;
    if (rawUrl in this.cachedTextures) {
      return this.cachedTextures[rawUrl];
    }

    console.debug(`[TextureManager] loading texture from ${path} ...`);
    const loadingPromise = (async () => {
      try {
        path = await resolvePath(path);
        const url = path.getResolvedPath();

        // Get the file path url, as firebase could add parameters to the url
        // https://...../skybox.hdr?alt=media
        let filePathUrl: string;
        try {
          const parsed = new URL(url);
          filePathUrl = parsed.origin + parsed.pathname;
        } catch {
          // Relative resolved URLs (e.g. `/.dist/.../foo.ktx2` in Vite dev) — use resolved
          // path for format detection, not the logical `@project/.../foo.png` initial path.
          filePathUrl = url;
        }

        const format = this.detectFormat(filePathUrl);

        // Handle serialized Genesys textures
        if (format === TextureFormat.TEXTURE_JSON) {
          const textureJsonObject = await this.loadSerializedTexture(path);
          const texture = WorldSerializer.importObject(textureJsonObject);
          if (texture instanceof UrlTexture) {
            // Wait for the texture to load, then return it
            await texture.loadPromise;
            return texture;
          } else {
            console.error(`Object loaded from ${path} is not a UrlTexture: ${texture}`);
            return null;
          }
        }

        // Handle all other texture formats
        let texture: THREE.Texture | null;
        if (format) {
          texture = await this.loadWithAppropriateLoader(url, format);
        } else {
          // Unknown format, try with default texture loader
          console.warn(`[TextureManager] Unknown texture format for ${path}, trying default loader`);
          texture = await this.textureLoader.loadAsync(url);
        }

        if (texture) {
          console.debug(`[TextureManager] texture loaded from ${path} (format: ${format ?? 'unknown'})`);
        }
        return texture;
      } catch (error) {
        console.error(`Failed to load texture from ${path}`, error);
        return null;
      }
    })();
    this.cachedTextures[rawUrl] = loadingPromise;
    return loadingPromise;
  }

  /**
   * Loads a serialized texture from JSON.
   *
   * @param path - The path of the texture JSON to load
   * @returns The texture JSON object
   */
  protected async loadSerializedTexture(path: AssetPath): Promise<any> {
    const storageProvider = projectInfo.storageProvider;
    if (!storageProvider) {
      throw new Error('No storage provider found');
    }
    const json = await storageProvider.downloadFileAsJson<any>(path);
    if (!json) {
      throw new Error(`Failed to download texture from ${path}`);
    }
    return json;
  }
}

